import React, { useRef, useState } from 'react';

import classNames from 'classnames';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import ArrowUpIcon from 'Assets/icons/arrow-up.svg';

/**
 * Drag a completed template here, or browse for it. The file is posted as
 * soon as it is chosen; there is no preview step. A rejected file lists every
 * row error the server reported, and nothing was imported, so the user can
 * correct the file and drop it again.
 *
 * onUploaded receives the response body; a 422 body is shown here.
 */
export default function TemplateDropzone({ id, uploadUrl, fieldName = 'file', onUploaded }) {
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [failure, setFailure] = useState(null);
    const inputRef = useRef(null);

    const upload = (file) => {
        if (!file || uploading) {
            return;
        }

        const body = new FormData();
        body.append(fieldName, file);

        setUploading(true);
        setFailure(null);

        axios
            .post(uploadUrl, body, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then(({ data }) => onUploaded(data))
            .catch((e) => {
                const response = e?.response?.data;
                const errors = response?.errors;

                setFailure({
                    fileName: file.name,
                    message: response?.message || $t('Something went wrong. Please try again.'),
                    rows: Array.isArray(errors) ? errors : [],
                    // A rejected upload field (wrong type, too large) answers
                    // with field errors rather than row errors.
                    fields: !Array.isArray(errors) && errors ? Object.values(errors).flat() : [],
                });
            })
            .finally(() => {
                setUploading(false);
                if (inputRef.current) {
                    inputRef.current.value = '';
                }
            });
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        upload(e.dataTransfer?.files?.[0]);
    };

    const browse = () => inputRef.current?.click();

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-3 tw-max-w-2xl">
            <div
                id={id}
                role="button"
                tabIndex={0}
                aria-busy={uploading}
                onClick={browse}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        browse();
                    }
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                className={classNames(
                    'tw-flex tw-flex-col tw-items-center tw-px-5 tw-py-9 tw-rounded-xl tw-border-2 tw-border-dashed tw-text-center tw-cursor-pointer tw-transition-colors',
                    dragging ? 'tw-border-blue tw-bg-blue-10' : 'tw-border-charcoal-25 tw-bg-charcoal-10 hover:tw-border-blue',
                )}
            >
                {uploading ? (
                    <LoadingSpinner ySpacing={false} size="sm" />
                ) : (
                    <ArrowUpIcon className="tw-w-6 tw-h-6 tw-mb-1.5 tw-text-charcoal-75" aria-hidden="true" />
                )}
                <p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-charcoal">
                    {uploading ? $t('Checking the file…') : $t('Drag the completed template here, or browse')}
                </p>
                <p className="tw-mb-0 tw-text-xs tw-text-charcoal-75">{$t('.xlsx using the InBudget upload format')}</p>
                <input
                    ref={inputRef}
                    type="file"
                    accept=".xlsx"
                    className="tw-hidden"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => upload(e.target.files?.[0])}
                />
            </div>

            {failure && (
                <div role="alert" className="tw-px-3.5 tw-py-2.5 tw-rounded-lg tw-border tw-border-red-25 tw-bg-red-10">
                    <p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-charcoal">{failure.message}</p>
                    <p className="tw-mb-0 tw-text-xs tw-text-charcoal-75">{failure.fileName}</p>
                    {(failure.rows.length > 0 || failure.fields.length > 0) && (
                        <ul className="tw-mt-2 tw-mb-0 tw-ps-0 tw-list-none tw-max-h-64 tw-overflow-y-auto">
                            {failure.rows.map((error, index) => (
                                <li key={`${error.row}-${index}`} className="tw-py-0.5 tw-text-0.5sm tw-text-charcoal-75">
                                    <span className="tw-font-semibold tw-text-charcoal">{$t('Row {{row}}', { row: error.row })}</span> {error.message}
                                </li>
                            ))}
                            {failure.fields.map((message) => (
                                <li key={message} className="tw-py-0.5 tw-text-0.5sm tw-text-charcoal-75">
                                    {message}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
